import {
  useQuery,
  useMutation,
  UseMutationResult,
  UseQueryResult,
} from "@tanstack/react-query";
import {
  deleteRecords,
  getExistingRecordId,
  getRowDataById,
  getRowDataList,
  saveRecord,
  updateARScheduleTypeConversionRecord,
} from "../pages/LandingPage/helpers";
import {
  RowData,
  ToastInfo,
  DeleteRecordRequest,
  SaveRecordRequest,
  UseDeleteRecordsParams,
  UseGetRowDataListParams,
  UseSaveRecordParams,
  UseRefetchRecordDataParams,
} from "../types";
import {
  ROW_DATA_KEYS,
  TOAST_MESSAGES,
  TRACK_RECORD_DATA_STATE_KEYS,
} from "../pages/LandingPage/constants";

export function useRefetchRowDataList({
  setupBaseRequestInfo,
  headerLabel,
  dispatch,
  setTableTitleInfo,
}: UseGetRowDataListParams): UseQueryResult<RowData[], unknown> {
  return useQuery({
    queryKey: ["refetchRowDataList"],
    queryFn: async () => {
      const updatedRowDataList = await getRowDataList(setupBaseRequestInfo);

      setTableTitleInfo?.(`${headerLabel} (${updatedRowDataList.length})`);

      dispatch({
        type: "UPDATE_ROW_DATA_REDUCER_STATE",
        payload: {
          rowDataList: updatedRowDataList,
          resetState: {
            rowDataList: updatedRowDataList,
          },
        },
      });

      return updatedRowDataList;
    },
    enabled: false,
  });
}

export function useSaveRecord({
  onSuccessCallback,
}: UseSaveRecordParams): UseMutationResult<
  ToastInfo,
  unknown,
  SaveRecordRequest
> {
  return useMutation({
    mutationFn: (request: SaveRecordRequest) => saveRecord(request),
    onSuccess: async (response) => {
      onSuccessCallback(response);
    },
  });
}

export function useARRecordScheduleTypeConversion({
  onSuccessCallback,
}: UseSaveRecordParams): UseMutationResult<
  ToastInfo,
  unknown,
  SaveRecordRequest
> {
  return useMutation({
    mutationFn: (request: SaveRecordRequest) =>
      updateARScheduleTypeConversionRecord(request),
    onSuccess: async (response) => {
      onSuccessCallback(response);
    },
  });
}

export function useDeleteRecords({
  onSuccessCallback,
}: UseDeleteRecordsParams): UseMutationResult<
  ToastInfo,
  unknown,
  DeleteRecordRequest
> {
  return useMutation({
    mutationFn: (request: DeleteRecordRequest) => deleteRecords(request),
    onSuccess: async (response) => {
      onSuccessCallback(response);
    },
  });
}

export function useRefetchRecordData({
  rowData,
  setupBaseRequestInfo,
  setIsRecordOpen,
  setLandingPageToast,
  dispatch,
}: UseRefetchRecordDataParams): UseQueryResult<RowData> {
  return useQuery({
    queryKey: ["recordDataById", rowData],
    queryFn: async () => {
      const recordId = getExistingRecordId(rowData);
      const rowDataById = await getRowDataById({
        ...setupBaseRequestInfo,
        recordId,
      });

      // if needed move below all logic inside using onSuccessCallback from where it is called
      if (Object.keys(rowDataById).length === 0) {
        setIsRecordOpen(false);
        setLandingPageToast(TOAST_MESSAGES.UANBLE_TO_FETCH_RECORD());
        return rowDataById;
      }

      // manually set the record id and created by to keep track throughout
      const {
        TRACK_EXISTING_ID,
        TRACK_EXISTING_CREATED_BY,
        TRACK_IS_CLONED_RECORD,
      } = TRACK_RECORD_DATA_STATE_KEYS;

      const updatedRowData: RowData = {
        ...rowDataById,
        // storing existing `id` for API calls (ex: onSuccessCallback)
        [TRACK_EXISTING_ID]: recordId,
        // storing existing `created_by` for New Entry verification (ex: isNewRecord)
        [TRACK_EXISTING_CREATED_BY]:
          rowData[ROW_DATA_KEYS.CREATED_BY] ??
          rowData[TRACK_EXISTING_CREATED_BY],
        // storing existing `isClonedRecord` if needed
        [TRACK_IS_CLONED_RECORD]: rowData[TRACK_IS_CLONED_RECORD],
      };

      dispatch({
        type: "UPDATE_ROW_DATA_REDUCER_STATE",
        payload: {
          rowData: updatedRowData,
          resetState: {
            rowData: updatedRowData,
          },
          deletedObjectsList: {},
        },
      });

      return updatedRowData;
    },
    enabled: false,
  });
}
